/**
 * PHA-1141 / PHA-1142: EHR-detected changes, reviewed item by item.
 *
 * The nightly EHR sync records what changed (problems, medications, vitals, labs) on one
 * `ehr_sync_runs` row per patient. The clinician now decides each item on its own — in the
 * Problems / Medications tabs, or in a goal's vitals table — and the Smart Hub row clears once
 * every item is decided. The identity and list-merge rules live in the backend
 * (ehr_change_review.py); this module is the display side: what a reading looks like, which
 * goal a reading belongs to, and how the Smart Hub summarises a run.
 */

export type EhrChangeKind =
  | 'problem_added'
  | 'problem_resolved'
  | 'med_added'
  | 'med_stopped'
  | 'med_dosage_changed'
  | 'med_removed'
  | 'observation';

export type EhrChangeDecisionValue = 'accepted' | 'rejected';

/** One undecided item of an EHR sync run (backend: ehr_change_review.run_items). */
export interface EhrChangeItem {
  change_kind: EhrChangeKind;
  bucket: string;
  item_key: string;
  ehr_sync_run_id: number;
  label: string | null;
  applicability?: string | null;
  /** ISO date the nightly sync first saw the change. */
  detected_at: string | null;
  icd10?: string | null;
  /** The EHR's version of the problem / medication row. */
  record?: Record<string, any>;
  previous_dosage?: string | null;
  document_type?: string;
  value?: number | string | null;
  unit?: string | null;
  date?: string | null;
  loinc?: string | null;
}

export interface EhrChangeAcknowledgment {
  change_kind: EhrChangeKind;
  item_key: string;
  ehr_sync_run_id: number;
  decision: EhrChangeDecisionValue;
  detail: Record<string, any> | null;
  acknowledged_by: string | null;
  acknowledged_by_name: string | null;
  acknowledged_ts: string | null;
}

export interface PatientEhrChanges {
  problems: EhrChangeItem[];
  medications: EhrChangeItem[];
  observations: EhrChangeItem[];
  acknowledgments: EhrChangeAcknowledgment[];
  latest_ehr_batch: { problems: any[]; medications: any[] };
  review_available: boolean;
}

export interface EhrChangeDecision {
  change_kind: EhrChangeKind;
  item_key: string;
  ehr_sync_run_id: number;
  decision: EhrChangeDecisionValue;
  edits?: Record<string, any>;
}

export interface ObservationAcknowledgment {
  by: string | null;
  by_name: string | null;
  ts: string | null;
  /** 'ehr_sync_run' = cleared with its whole sync run (e.g. on care plan approval). */
  via: 'item' | 'ehr_sync_run';
}

export interface ObservationPendingRef {
  change_kind: EhrChangeKind;
  item_key: string;
  ehr_sync_run_id: number;
}

/** One vital / lab reading (backend: get_patient_observation_history). */
export interface ObservationReading {
  document_type: string;
  value: number | null;
  unit: string | null;
  date: string;
  source: 'ehr' | 'manual';
  loinc: string | null;
  first_seen_ts: string | null;
  entered_by: string | null;
  acknowledgment: ObservationAcknowledgment | null;
  pending: ObservationPendingRef[] | null;
}

export interface BaselineReading {
  value: number;
  unit?: string | null;
  date?: string | null;
  source?: string | null;
}

export interface TargetBaseline {
  care_plan_uploaded_ts: string | null;
  vitals: Record<string, BaselineReading> | null;
}

export interface ObservationHistory {
  readings: ObservationReading[];
  baselines: { approved: TargetBaseline | null; draft: TargetBaseline | null };
  review_available: boolean;
}

export const BP_SYSTOLIC = 'blood_pressure_systolic';
export const BP_DIASTOLIC = 'blood_pressure_diastolic';

// ---------------------------------------------------------------------------
// Which readings belong under which goal.
// ---------------------------------------------------------------------------

export interface GoalVitalGroup {
  id: string;
  label: string;
  documentTypes: string[];
}

const GROUPS: Record<string, GoalVitalGroup> = {
  blood_pressure: { id: 'blood_pressure', label: 'Blood pressure', documentTypes: [BP_SYSTOLIC, BP_DIASTOLIC] },
  bmi: { id: 'bmi', label: 'BMI and weight', documentTypes: ['BMI', 'weight'] },
  hba1c: { id: 'hba1c', label: 'HbA1c', documentTypes: ['hba1c'] },
  ldl: { id: 'ldl', label: 'LDL cholesterol', documentTypes: ['lipid_ldl'] },
  hdl: { id: 'hdl', label: 'HDL cholesterol', documentTypes: ['lipid_hdl'] },
  triglycerides: { id: 'triglycerides', label: 'Triglycerides', documentTypes: ['lipid_triglycerides'] },
  total_cholesterol: { id: 'total_cholesterol', label: 'Total cholesterol', documentTypes: ['lipid_total_cholesterol'] },
  renal: { id: 'renal', label: 'Kidney function', documentTypes: ['egfr', 'creatinine'] },
  heart_rate: { id: 'heart_rate', label: 'Heart rate', documentTypes: ['heart_rate'] },
  oxygen: { id: 'oxygen', label: 'Oxygen saturation', documentTypes: ['oxygen_saturation'] },
  tsh: { id: 'tsh', label: 'TSH', documentTypes: ['tsh'] },
  nt_probnp: { id: 'nt_probnp', label: 'NT-proBNP', documentTypes: ['nt_probnp'] },
};

/**
 * The readings a care-plan goal is measured by, from the goal's free-text `measure`.
 *
 * Goals are LLM-written ("Blood Pressure", "HbA1c", "LDL Cholesterol"), readings are typed
 * (`blood_pressure_systolic`, `hba1c`), and nothing links the two, so this map is that link —
 * the only one. Order matters: LDL / HDL / triglycerides before the generic "cholesterol".
 * An unrecognised measure gets NO table rather than a guessed one; "Hypoglycemia Management"
 * must not pick up HbA1c just because it contains "glyc".
 */
export function goalVitalGroup(measure: string | null | undefined): GoalVitalGroup | null {
  const m = String(measure || '').toLowerCase();
  if (!m.trim()) return null;
  if (/blood\s*pressure|\bbp\b/.test(m)) return GROUPS.blood_pressure;
  if (/\bbmi\b|body mass|\bweight\b/.test(m)) return GROUPS.bmi;
  if (/a1c|glycated|glycosylated|blood\s*(sugar|glucose)/.test(m)) return GROUPS.hba1c;
  if (/\bldl\b/.test(m)) return GROUPS.ldl;
  if (/\bhdl\b/.test(m)) return GROUPS.hdl;
  if (/triglycerid/.test(m)) return GROUPS.triglycerides;
  if (/cholesterol/.test(m)) return GROUPS.total_cholesterol;
  if (/renal|kidney|\begfr\b|\bgfr\b|creatinine/.test(m)) return GROUPS.renal;
  if (/heart\s*rate|\bpulse\b/.test(m)) return GROUPS.heart_rate;
  if (/oxygen|spo2|o2\s*sat/.test(m)) return GROUPS.oxygen;
  if (/\btsh\b|thyroid/.test(m)) return GROUPS.tsh;
  if (/probnp|\bbnp\b/.test(m)) return GROUPS.nt_probnp;
  return null;
}

// ---------------------------------------------------------------------------
// Displaying a reading.
// ---------------------------------------------------------------------------

const MEASURE_LABELS: Record<string, string> = {
  [BP_SYSTOLIC]: 'Systolic BP',
  [BP_DIASTOLIC]: 'Diastolic BP',
  BMI: 'BMI',
  height: 'Height',
  weight: 'Weight',
  heart_rate: 'Heart rate',
  temperature: 'Temperature',
  respiratory_rate: 'Respiratory rate',
  oxygen_saturation: 'O2 saturation',
  hba1c: 'HbA1c',
  lipid_total_cholesterol: 'Total cholesterol',
  lipid_ldl: 'LDL',
  lipid_hdl: 'HDL',
  lipid_triglycerides: 'Triglycerides',
  egfr: 'eGFR',
  creatinine: 'Creatinine',
  tsh: 'TSH',
  nt_probnp: 'NT-proBNP',
};

export const measureLabel = (documentType: string | null | undefined): string =>
  MEASURE_LABELS[String(documentType || '')] || String(documentType || '');

// Athena spells UCUM units out ("millimeter of mercury"); clinicians read the short form.
const UNIT_SHORT: Record<string, string> = {
  'millimeter of mercury': 'mmHg',
  'mm[hg]': 'mmHg',
  'kilogram / (meter ^ 2)': 'kg/m²',
  'kg/m2': 'kg/m²',
  'beats / minute': 'bpm',
  '/min': '/min',
  'breaths / minute': '/min',
  'percent': '%',
  'milligram per deciliter': 'mg/dL',
  'mg/dl': 'mg/dL',
};

export const shortUnit = (unit: string | null | undefined): string => {
  const raw = String(unit || '').trim();
  return UNIT_SHORT[raw.toLowerCase()] || raw;
};

const toNumber = (value: unknown): number | null => {
  const n = typeof value === 'number' ? value : parseFloat(String(value ?? ''));
  return Number.isFinite(n) ? n : null;
};

const round = (n: number, places: number) => {
  const f = 10 ** places;
  return Math.round(n * f) / f;
};

/**
 * One reading as clinicians read it. Weight is shown in pounds whatever the EHR stored it in —
 * Athena sends grams (80739.44 g on dev), the wizard stores kilograms — because a US chart
 * reads weight in pounds (PHA-1076). Height likewise in feet and inches.
 */
export function formatReadingValue(documentType: string, value: unknown, unit: string | null | undefined): string {
  const n = toNumber(value);
  if (n === null) return '—';
  const u = String(unit || '').trim().toLowerCase();
  if (documentType === 'weight') {
    let pounds = n;
    if (u === 'g' || u === 'gram' || u === 'grams') pounds = n / 453.59237;
    else if (u === 'kg' || u === 'kilogram' || u === 'kilograms' || !u) pounds = n * 2.20462;
    return `${round(pounds, 1)} lb`;
  }
  if (documentType === 'height') {
    let inches = n / 2.54; // cm by default
    if (u === 'm' || u === 'meter' || u === 'meters') inches = (n * 100) / 2.54;
    else if (u === 'in' || u === 'inch' || u === 'inches' || u === '[in_i]') inches = n;
    const feet = Math.floor(inches / 12);
    return `${feet}′ ${Math.round(inches - feet * 12)}″`;
  }
  const places = documentType === 'BMI' || documentType === 'hba1c' || documentType === 'creatinine' ? 1 : 0;
  const shown = Number.isInteger(n) ? n : round(n, places || 1);
  const su = shortUnit(unit);
  return su ? `${shown} ${su}` : String(shown);
}

/** A row of a vitals table: one reading, or a systolic/diastolic pair taken the same day. */
export interface VitalTableRow {
  key: string;
  date: string;
  label: string;
  display: string;
  documentTypes: string[];
  readings: ObservationReading[];
  source: 'ehr' | 'manual';
  pending: ObservationPendingRef[];
  acknowledgment: ObservationAcknowledgment | null;
  /** The reading the goal's target was calculated from (PHA-1142 requirement 3). */
  isBaseline?: boolean;
}

const rowFromReadings = (readings: ObservationReading[], label: string, display: string): VitalTableRow => {
  const pending = readings.flatMap((r) => r.pending || []);
  const acknowledgment = pending.length > 0 ? null : readings.find((r) => r.acknowledgment)?.acknowledgment || null;
  return {
    key: readings.map((r) => `${r.document_type}|${r.date}|${r.value}`).join('+'),
    date: readings[0].date,
    label,
    display,
    documentTypes: readings.map((r) => r.document_type),
    readings,
    source: readings.some((r) => r.source === 'ehr') ? 'ehr' : 'manual',
    pending,
    acknowledgment,
  };
};

/**
 * The rows of one goal's vitals table, newest first. Systolic and diastolic readings taken the
 * same day become one "145/92 mmHg" row — they are one measurement, and acknowledging it must
 * acknowledge both halves.
 */
export function buildVitalTableRows(readings: ObservationReading[], documentTypes: string[]): VitalTableRow[] {
  const wanted = new Set(documentTypes);
  const mine = (readings || []).filter((r) => wanted.has(r.document_type));
  const rows: VitalTableRow[] = [];
  const used = new Set<ObservationReading>();

  if (wanted.has(BP_SYSTOLIC) || wanted.has(BP_DIASTOLIC)) {
    const diastolicByDate = new Map<string, ObservationReading>();
    mine.filter((r) => r.document_type === BP_DIASTOLIC).forEach((r) => {
      if (!diastolicByDate.has(r.date)) diastolicByDate.set(r.date, r);
    });
    mine.filter((r) => r.document_type === BP_SYSTOLIC).forEach((sys) => {
      const dia = diastolicByDate.get(sys.date);
      if (dia && !used.has(dia)) {
        used.add(sys);
        used.add(dia);
        rows.push(rowFromReadings([sys, dia], 'Blood pressure', `${toNumber(sys.value) ?? '—'}/${toNumber(dia.value) ?? '—'} mmHg`));
      }
    });
  }
  const multiple = new Set(mine.map((r) => r.document_type)).size > 1;
  mine.forEach((r) => {
    if (used.has(r)) return;
    const label = measureLabel(r.document_type);
    const value = formatReadingValue(r.document_type, r.value, r.unit);
    rows.push(rowFromReadings([r], label, multiple || r.document_type.startsWith('blood_pressure') ? `${label} ${value}` : value));
  });
  rows.sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
  return rows;
}

/**
 * The goal's table with the target baseline pinned as the FIRST row (PHA-1142 requirement 3):
 * the reading the target was calculated from, then everything else newest first. When that
 * reading is also in the history it is moved, not duplicated. Only BP and BMI targets are
 * calculated from a reading, so only those groups ever have a baseline.
 */
export function withBaselineRow(
  rows: VitalTableRow[],
  group: GoalVitalGroup,
  baseline: Record<string, BaselineReading> | null | undefined,
): VitalTableRow[] {
  if (!baseline) return rows;
  let wantedTypes: string[] = [];
  if (group.id === 'blood_pressure' && baseline[BP_SYSTOLIC] && baseline[BP_DIASTOLIC]) {
    wantedTypes = [BP_SYSTOLIC, BP_DIASTOLIC];
  } else if (group.id === 'bmi' && baseline.BMI) {
    wantedTypes = ['BMI'];
  }
  if (wantedTypes.length === 0) return rows;

  const matches = (row: VitalTableRow) =>
    wantedTypes.every((t) => {
      const reading = row.readings.find((r) => r.document_type === t);
      const b = baseline[t];
      return !!reading && reading.date === (b.date || '').slice(0, 10) && toNumber(reading.value) === toNumber(b.value);
    }) && row.readings.length === wantedTypes.length;

  const idx = rows.findIndex(matches);
  if (idx >= 0) {
    const pinned = { ...rows[idx], isBaseline: true };
    return [pinned, ...rows.slice(0, idx), ...rows.slice(idx + 1)];
  }
  // The reading is not in the history any more (or never was, e.g. a later-rejected entry):
  // show what the snapshot recorded.
  const synthetic: ObservationReading[] = wantedTypes.map((t) => ({
    document_type: t,
    value: toNumber(baseline[t].value),
    unit: baseline[t].unit ?? null,
    date: (baseline[t].date || '').slice(0, 10),
    source: baseline[t].source === 'manual' ? 'manual' : 'ehr',
    loinc: null,
    first_seen_ts: null,
    entered_by: null,
    acknowledgment: null,
    pending: null,
  }));
  const display =
    group.id === 'blood_pressure'
      ? `${toNumber(baseline[BP_SYSTOLIC].value)}/${toNumber(baseline[BP_DIASTOLIC].value)} mmHg`
      : formatReadingValue('BMI', baseline.BMI.value, baseline.BMI.unit);
  return [{ ...rowFromReadings(synthetic, group.label, display), isBaseline: true }, ...rows];
}

/**
 * One line naming the reading a goal's target was calculated from, for the draft review
 * (PHA-1142 requirement 3 during creation, and requirement 4's "new vital shown under the
 * appropriate goal" on regeneration). Null for goals whose target is not calculated from a
 * reading, or when the plan fell back to the default target.
 */
export function describeBaselineForGoal(
  measure: string | null | undefined,
  baseline: Record<string, BaselineReading> | null | undefined,
): string | null {
  const group = goalVitalGroup(measure);
  if (!group || !baseline) return null;
  let value: string | null = null;
  let reading: BaselineReading | undefined;
  if (group.id === 'blood_pressure' && baseline[BP_SYSTOLIC] && baseline[BP_DIASTOLIC]) {
    reading = baseline[BP_SYSTOLIC];
    value = `BP ${toNumber(baseline[BP_SYSTOLIC].value)}/${toNumber(baseline[BP_DIASTOLIC].value)} mmHg`;
  } else if (group.id === 'bmi' && baseline.BMI) {
    reading = baseline.BMI;
    value = `BMI ${formatReadingValue('BMI', baseline.BMI.value, baseline.BMI.unit)}`;
  }
  if (!value || !reading) return null;
  const source = reading.source === 'manual' ? 'entered' : 'EHR';
  const date = shortDate(reading.date);
  return [value, source, date].filter(Boolean).join(' · ');
}

/** Decisions that acknowledge every pending half of a table row. */
export const decisionsForRow = (row: VitalTableRow): EhrChangeDecision[] =>
  row.pending.map((p) => ({
    change_kind: p.change_kind,
    item_key: p.item_key,
    ehr_sync_run_id: p.ehr_sync_run_id,
    decision: 'accepted' as const,
  }));

// ---------------------------------------------------------------------------
// The Smart Hub row.
// ---------------------------------------------------------------------------

export interface EhrChangeSummaryLine {
  section: 'problems' | 'medications' | 'vitals';
  text: string;
  /** The short form used when the list is too long to show in full (PHA-1141 meds rule). */
  short: string;
}

type SummaryItem = Record<string, any>;

const itemDate = (item: SummaryItem, fallback?: string | null): string | null =>
  (item?.detected_at as string) || fallback || null;

const shortDate = (iso: string | null | undefined): string => {
  const m = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${parseInt(m[2], 10)}/${parseInt(m[3], 10)}/${m[1]}` : '';
};

const withDate = (text: string, iso: string | null) => (iso ? `${text} · ${shortDate(iso)}` : text);

/**
 * The actual changes of a run, one line each, for the Smart Hub row. PHA-1142: "No change
 * should be mentioned without a reference to the change itself".
 */
export function describeEhrChanges(changeSummary: Record<string, SummaryItem[] | undefined> | null | undefined, runDate?: string | null): EhrChangeSummaryLine[] {
  const cs = changeSummary || {};
  const lines: EhrChangeSummaryLine[] = [];
  const push = (section: EhrChangeSummaryLine['section'], text: string, short: string, date: string | null) =>
    lines.push({ section, text: withDate(text, date), short: withDate(short, date) });

  (cs.problems_added || []).forEach((p) => {
    const name = p.record?.problem || p.label || p.icd10 || 'Problem';
    push('problems', `New problem: ${name}${p.icd10 ? ` (${p.icd10})` : ''}`, name, itemDate(p, runDate));
  });
  (cs.problems_resolved || []).forEach((p) => {
    const name = p.record?.problem || p.label || p.icd10 || 'Problem';
    push('problems', `Resolved: ${name}`, name, itemDate(p, runDate));
  });
  (cs.meds_added || []).forEach((m) => {
    const name = m.record?.medication || m.label || 'Medication';
    const dose = m.record?.dosage ? ` ${m.record.dosage}` : '';
    push('medications', `New medication: ${name}${dose}`, name, itemDate(m, runDate));
  });
  (cs.meds_dosage_changed || []).forEach((m) => {
    const name = m.record?.medication || m.label || 'Medication';
    push('medications', `Dose changed: ${name} ${m.previous_dosage || '?'} → ${m.record?.dosage || '?'}`, name, itemDate(m, runDate));
  });
  (cs.meds_changed || []).forEach((m) => {
    const name = m.record?.medication || m.label || 'Medication';
    push('medications', `Stopped: ${name}`, name, (m.record?.end_date as string) || itemDate(m, runDate));
  });
  (cs.meds_removed || []).forEach((m) => {
    const name = m.record?.medication || m.label || 'Medication';
    push('medications', `Removed from EHR: ${name}`, name, itemDate(m, runDate));
  });

  const observations = [...(cs.vitals_added || []), ...(cs.labs_added || [])] as SummaryItem[];
  const asReadings: ObservationReading[] = observations.map((o) => ({
    document_type: String(o.document_type || ''),
    value: toNumber(o.value),
    unit: o.unit ?? null,
    date: String(o.date || '').slice(0, 10),
    source: 'ehr',
    loinc: o.loinc ?? null,
    first_seen_ts: null,
    entered_by: null,
    acknowledgment: null,
    pending: null,
  }));
  const allTypes = Array.from(new Set(asReadings.map((r) => r.document_type)));
  buildVitalTableRows(asReadings, allTypes).forEach((row) => {
    const text = row.label === 'Blood pressure' || row.display.startsWith(row.label) ? row.display : `${row.label} ${row.display}`;
    const label = row.label === 'Blood pressure' ? `BP ${row.display}` : text;
    push('vitals', label, label, row.date || null);
  });
  return lines;
}

export const summaryHasItems = (changeSummary: Record<string, any> | null | undefined): boolean =>
  describeEhrChanges(changeSummary).length > 0;

/**
 * Lines to show in a row of limited height. Past `max`, medication lines collapse to "name ·
 * date" (the PHA-1141 rule: "if too long shorten it to fit — just names of meds with change,
 * and dates"), and anything still over is counted rather than shown.
 */
export function fitSummaryLines(lines: EhrChangeSummaryLine[], max = 4): { shown: string[]; hidden: number } {
  if (lines.length <= max) return { shown: lines.map((l) => l.text), hidden: 0 };
  const compact = lines.map((l) => (l.section === 'medications' ? l.short : l.text));
  return { shown: compact.slice(0, max), hidden: compact.length - max };
}

// ---------------------------------------------------------------------------
// Deep-linking from the Smart Hub into the chart.
// ---------------------------------------------------------------------------

export type EhrChangeFocusSection = 'problems' | 'medications' | 'vitals';

export interface EhrChangeFocus {
  section: EhrChangeFocusSection;
  /** For 'vitals': the reading types the alert was about, so their goal tables open. */
  documentTypes?: string[];
}

const focusKey = (patientId: string) => `phanova:ehrChangeFocus:${patientId}`;
const FOCUS_TTL_MS = 5 * 60 * 1000;

/**
 * The chart opens through the tab manager, which only carries a tab index. What to expand and
 * scroll to rides alongside in sessionStorage, consumed once by the chart. Stale intents
 * (a tab opened long after the click) are ignored.
 */
export function setEhrChangeFocus(patientId: string, focus: EhrChangeFocus): void {
  try {
    sessionStorage.setItem(focusKey(patientId), JSON.stringify({ ...focus, at: Date.now() }));
  } catch {
    // Private mode / storage disabled: the tab still opens, just without the scroll.
  }
}

export function consumeEhrChangeFocus(patientId: string): EhrChangeFocus | null {
  try {
    const raw = sessionStorage.getItem(focusKey(patientId));
    if (!raw) return null;
    sessionStorage.removeItem(focusKey(patientId));
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.at !== 'number' || Date.now() - parsed.at > FOCUS_TTL_MS) return null;
    return { section: parsed.section, documentTypes: parsed.documentTypes };
  } catch {
    return null;
  }
}

/** Whether a recorded acknowledgment applies to a problem / medication row on the chart list. */
export function acknowledgmentForListItem(
  acknowledgments: EhrChangeAcknowledgment[],
  documentType: 'problem_list' | 'medication_list',
  item: Record<string, any>,
): EhrChangeAcknowledgment | null {
  const norm = (s: unknown) => String(s || '').toLowerCase().replace(/\s+/g, ' ').trim();
  const code = String(item?.icd10_code || '').toUpperCase();
  const name = norm(documentType === 'problem_list' ? item?.problem : item?.medication);
  const kinds: EhrChangeKind[] =
    documentType === 'problem_list'
      ? ['problem_added', 'problem_resolved']
      : ['med_added', 'med_stopped', 'med_dosage_changed'];
  let latest: EhrChangeAcknowledgment | null = null;
  for (const ack of acknowledgments || []) {
    if (ack.decision !== 'accepted' || !kinds.includes(ack.change_kind)) continue;
    const rec = (ack.detail && ack.detail.record) || {};
    const matches =
      documentType === 'problem_list'
        ? (code && String(rec.icd10_code || '').toUpperCase() === code) || (name && norm(rec.problem) === name)
        : name && norm(rec.medication) === name;
    if (matches && (!latest || String(ack.acknowledged_ts || '') > String(latest.acknowledged_ts || ''))) {
      latest = ack;
    }
  }
  return latest;
}
